/**
 * Features marketing page (`/features`) — GitHub issue "Create React
 * components for static marketing pages using vite-react-ssg".
 * Statically generated; no client-side data fetching or auth dependency.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MarketingLayout from '@/components/marketing/MarketingLayout';
import SeoHead from '@/components/marketing/SeoHead';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

const FEATURE_KEYS = [
  'screening',
  'ranking',
  'workflows',
  'collaboration',
  'analytics',
  'integrations',
] as const;

export default function FeaturesPage(): JSX.Element {
  const { t } = useTranslation();

  return (
    <MarketingLayout>
      <SeoHead
        title={t('marketing.features.title')}
        description={t('marketing.features.description')}
        path="/features"
      />

      <section className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          {t('marketing.features.heading')}
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-xl text-muted-foreground">
          {t('marketing.features.subheading')}
        </p>
      </section>

      <section aria-label={t('marketing.features.heading')} className="border-t bg-muted/30">
        <div className="container mx-auto px-4 py-16">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURE_KEYS.map((key) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle>{t(`marketing.features.list.${key}.title`)}</CardTitle>
                  <CardDescription>
                    {t(`marketing.features.list.${key}.description`)}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 text-center">
        <Button size="lg" asChild>
          <Link to="/getstarted">{t('marketing.features.cta')}</Link>
        </Button>
      </section>
    </MarketingLayout>
  );
}
